/**
 * Deliverable deadlines - the one calculation every screen uses.
 *
 * Five screens each carried their own copy, and they disagreed:
 *   - the client tab subtracted the turnaround period instead of adding it,
 *     so every deadline fell before the shoot - 30 October plus a 25-day
 *     photo turnaround showed 5 October, not 24 November (QA DEADLINE-01);
 *   - the client tab had no Performance Film case and used album days, while
 *     the cinema tracker used a fixed 45 days rather than the configured
 *     period (QA DEADLINE-02);
 *   - the photo, cinema and pre-wedding trackers fell back to a hard-coded
 *     45 days for anything they did not recognise.
 *
 * The backend applies the same rules in backend/utils/deliverableDeadline.js.
 * Keep the two in step - both test suites check the same table.
 *
 * Dates. A deliverable's date is a "YYYY-MM-DD" string. The old arithmetic
 * read it as UTC midnight and then added days in local time, which lands a
 * day early anywhere west of UTC. Here the arithmetic is done on the calendar
 * date itself and the result is returned as "YYYY-MM-DD".
 */
import dayjs from "dayjs";

// Exact deliverable names -> deadline setting. Every other name is an album:
// albums are the only deliverables named freely, after the album options.
const PERIOD_BY_NAME = {
  "photos": "photo",
  "promo": "promo",
  "long film": "longFilm",
  "reel": "reel",
  "performance film": "performanceFilms",
  "pre-wedding photos": "preWedPhoto",
  "pre-wedding videos": "preWedVideo",
};

/** Turnaround days for a deliverable under the given settings, or null. */
export function turnaroundDays(deliverable, settings) {
  if (!settings || !deliverable) return null;
  const key = deliverable.isAlbum
    ? "album"
    : PERIOD_BY_NAME[String(deliverable.deliverableName || "").trim().toLowerCase()] || "album";
  const days = Number(settings[key]);
  return Number.isFinite(days) ? days : null;
}

/** The deadline as "YYYY-MM-DD", or null when it cannot be worked out. */
export function deliverableDeadline(deliverable, settings) {
  const days = turnaroundDays(deliverable, settings);
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(deliverable?.date || ""));
  if (days === null || !match) return null;
  const [, y, m, d] = match.map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** The deadline formatted for display, or an explicit "—". */
export function formatDeadline(deliverable, settings, format = "DD-MMM-YYYY") {
  const deadline = deliverableDeadline(deliverable, settings);
  // dayjs reads a bare "YYYY-MM-DD" as a local date, so no timezone shift.
  return deadline ? dayjs(deadline).format(format) : "—";
}

/** True once the deadline day has passed, in the viewer's own calendar. */
export function isDeadlinePassed(deliverable, settings, now = new Date()) {
  const deadline = deliverableDeadline(deliverable, settings);
  return deadline ? dayjs(deadline).isBefore(dayjs(now).startOf("day")) : false;
}
